// Konec akční ceny u ceny produktu („do 5. 9." / odpočet „za 2 d 5 h").
//
// Shoptet umí akční cenu časově omezit (admin → Akční cena → platnost), ale na
// frontend datum konce NIKDE nepošle: DOM detailu, karty ani košíku ho nenese,
// dataLayer `shoptet.product` i `cartInfo.cartItems[]` taky ne (sonda
// `ops/sweeps/2026-08-26-akcni-cena-platnost/`). Jediný zdroj je Private API
// (`variants[].actionPrice.toDate`), které čte náš vyhledávací indexer → pole
// `action_to` v indexu → `/specs?codes=` (položka `action.to`) a na kartě
// výsledků hledání rovnou `action_to`.
//
// Zásady (rozhodnutí Tomáše 2026-08-26):
//   • prvek stojí VŽDY u ceny — spodní pás cenové pilulky na kartě, řádek pod
//     cenou v detailu / sticky liště / modalu, pod jednotkovou cenou v košíku;
//   • déle než 72 h do konce = datum („do 5. 9."), pod 72 h = odpočet ve
//     třech hustotách (2. kolo 2026-08-26 — HH:MM „53:10" čtenář četl jako
//     čas dne): karta „za 2 d 5 h" · „za 5 h" · „za 42 min" (minuty až
//     v poslední hodině, na kartě není místo), ostatní hostitelé „končí za
//     2 d 5 h" · „5 h 10 min" · „42 min", detail slovně „Akce končí za 2 dny
//     5 hodin" · „5 hodin 10 minut" · „42 minut";
//   • kreslí se JEN tam, kde stránka reálně nese akční cenu (nativní
//     přeškrtnutá cena / sleva v dataLayeru) — index jede po 15 min za
//     platformou a cena na stránce je zdroj pravdy; prošlý konec = nic;
//   • bez cenové konfigurace vyhledávání (`getRetailConfig()`: Ú0, velkoobchod — akce v indexu je ceníku 1) = nic —
//     `/specs` sice od 2026-09-14 jede i velkoobchodu (Bestseller, chipy), ale
//     `action` je akce ceníku 1 a VO ceník akční ceny nemá.
//
// Platnost `toDate` je čisté datum shopu, oba konce VČETNĚ → konec akce je
// půlnoc PO tomto dni (lokální čas prohlížeče; shop je český).

import { icon } from './icon.js';
import { getRetailConfig } from './searchConfig.js';
import { primeSpecs, specsFor } from './specsCache.js';
import L from '../locale/cs/saleEnd.js';

/** Práh, od kterého se místo data kreslí odpočet (72 h). */
export const COUNTDOWN_MS = 72 * 60 * 60 * 1000;

/** Tik odpočtu — minutový krok stačí obnovovat po 30 s (půl minuty rezerva). */
const TICK_MS = 30 * 1000;

export const LABELS = {
  until: L.until,
  untilLong: L.untilLong,
  endsCard: L.endsCard,
  ends: L.ends,
  endsLong: L.endsLong,
};

/** Hustoty znění, viz `formatCountdown`. */
export const VARIANTS = ['card', 'short', 'long'];

/** Genitiv dnů v týdnu („do soboty 5. 9."), index = `Date#getDay()`. */
const WEEKDAY_GENITIVE = L.weekdayGenitive;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Konec akce z `YYYY-MM-DD`: půlnoc po daném dni (den je včetně), lokální čas.
 * Nevalidní vstup → `null`.
 */
export function saleEndOf(toDate) {
  const m = DATE_RE.exec(String(toDate || ''));
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const end = new Date(y, mo - 1, d + 1, 0, 0, 0, 0);
  return isNaN(end.getTime()) ? null : end;
}

/** „5. 9." — bez roku, s mezerou (typografie webu). */
export function shortDate(date) {
  return date.getDate() + '. ' + (date.getMonth() + 1) + '.';
}

/** Části odpočtu: dny / hodiny / minuty, zaokrouhleno dolů na celé minuty. */
export function countdownParts(ms) {
  const total = Math.max(0, Math.floor(ms / 60000));
  return { d: Math.floor(total / 1440), h: Math.floor((total % 1440) / 60), min: total % 60, total: total };
}

/**
 * Text odpočtu ve třech hustotách (bez předložky):
 *   card   „2 d 5 h" · „5 h" · „42 min"          — karta: bez minut, ty až pod 1 h
 *   short  „2 d 5 h" · „5 h 10 min" · „42 min"   — sticky lišta, modal, košík, našeptávač
 *   long   „2 dny 5 hodin" · „5 hodin 10 minut" · „42 minut" — detail
 * Nulová složka se vynechá („2 d", „5 hodin"); poslední minuta se drží na „1 min",
 * ať odpočet nikdy neukáže nulu, dokud akce běží.
 */
export function formatCountdown(ms, variant) {
  const p = countdownParts(ms);
  const v = variant || 'short';
  if (p.total >= 1440) {
    if (v === 'long') {
      return p.d + ' ' + L.days(p.d) + (p.h ? ' ' + p.h + ' ' + L.hours(p.h) : '');
    }
    return p.d + ' ' + L.dayShort + (p.h ? ' ' + p.h + ' ' + L.hourShort : '');
  }
  if (p.total >= 60) {
    if (v === 'long') {
      return p.h + ' ' + L.hours(p.h) + (p.min ? ' ' + p.min + ' ' + L.minutes(p.min) : '');
    }
    if (v === 'card') return p.h + ' ' + L.hourShort;
    return p.h + ' ' + L.hourShort + (p.min ? ' ' + p.min + ' ' + L.minShort : '');
  }
  const m = Math.max(1, p.min);
  return v === 'long' ? m + ' ' + L.minutes(m) : m + ' ' + L.minShort;
}

/**
 * Stav zobrazení pro `toDate` v čase `now`, nebo `null` (bez data / prošlé).
 * `text` = odpočet v hustotě short (bez předložky) / datum; `card`, `short`,
 * `long` = hotová znění pro hostitele; `times` (jen odpočet) = text po
 * hustotách pro render se zvýrazněným časem.
 *
 * @returns {{mode: 'date'|'countdown', end: Date, remaining: number, text: string,
 *            card: string, short: string, long: string,
 *            times?: {card: string, short: string, long: string}}|null}
 */
export function saleEndState(toDate, now) {
  const end = saleEndOf(toDate);
  if (!end) return null;
  const at = typeof now === 'number' ? now : now instanceof Date ? now.getTime() : Date.now();
  const remaining = end.getTime() - at;
  if (remaining <= 0) return null;

  if (remaining <= COUNTDOWN_MS) {
    const times = {
      card: formatCountdown(remaining, 'card'),
      short: formatCountdown(remaining, 'short'),
      long: formatCountdown(remaining, 'long'),
    };
    return {
      mode: 'countdown',
      end: end,
      remaining: remaining,
      text: times.short,
      times: times,
      card: LABELS.endsCard + ' ' + times.card,
      short: LABELS.ends + ' ' + times.short,
      long: LABELS.endsLong + ' ' + times.long,
    };
  }
  // poslední den akce = den PŘED půlnocí konce
  const last = new Date(end.getTime() - 1);
  const text = shortDate(last);
  const short = LABELS.until + ' ' + text;
  return {
    mode: 'date',
    end: end,
    remaining: remaining,
    text: text,
    card: short,
    short: short,
    long: LABELS.untilLong + ' ' + WEEKDAY_GENITIVE[last.getDay()] + ' ' + text,
  };
}

function variantOf(el) {
  const v = el.dataset.bsSaleVariant;
  return VARIANTS.indexOf(v) >= 0 ? v : 'short';
}

// ---------------------------------------------------------------------------
// Render + tik
// ---------------------------------------------------------------------------

const LIVE = new Set();
let timer = null;

function paint(el, state) {
  const variant = variantOf(el);
  const text = el.querySelector('.bs-sale-end__text');
  if (!text) return;
  // Idempotence: stejný text = žádný zápis. Každý zápis je childList mutace,
  // kterou sledují observery košíku i výpisu (code review 2026-09-09, B1-02).
  const signature = state.mode + '|' + (state.mode === 'countdown' ? state.times[variant] : state[variant]);
  if (el.dataset.bsSalePainted === signature) return;
  el.dataset.bsSalePainted = signature;
  el.classList.remove('bs-sale-end--date', 'bs-sale-end--countdown');
  el.classList.add('bs-sale-end--' + state.mode);
  text.textContent = '';
  if (state.mode === 'countdown') {
    const label = variant === 'long' ? LABELS.endsLong : variant === 'card' ? LABELS.endsCard : LABELS.ends;
    text.appendChild(document.createTextNode(label + ' '));
    const time = document.createElement('b');
    time.className = 'bs-sale-end__time';
    time.textContent = state.times[variant];
    text.appendChild(time);
  } else {
    text.textContent = state[variant];
  }
}

function tickAll() {
  const now = Date.now();
  LIVE.forEach(function (el) {
    if (!el.isConnected) {
      LIVE.delete(el);
      return;
    }
    const state = saleEndState(el.dataset.bsSaleTo, now);
    if (!state) {
      // akce doběhla pod rukama: prvek zmizí, hostitel se dozví eventem
      LIVE.delete(el);
      const host = el.parentNode;
      el.remove();
      if (host) host.dispatchEvent(new CustomEvent('bs:sale-end-expired', { bubbles: true }));
      return;
    }
    paint(el, state);
  });
  if (!LIVE.size && timer) {
    clearInterval(timer);
    timer = null;
  }
}

/**
 * Prvek „⏱ do 5. 9." / „⏱ končí za 5 h 10 min" pro `toDate`, nebo `null`, když
 * není co kreslit. `variant`: `card` (karta — „za 5 h"), `short` (výchozí),
 * `long` (detail — „Akční cena platí do soboty 5. 9." / „Akce končí za 5 hodin
 * 10 minut"); `long: true` je alias pro `variant: 'long'`.
 * Prvek se sám tikáním aktualizuje a po vypršení odstraní.
 */
export function createSaleEnd(toDate, opts) {
  const o = opts || {};
  const state = saleEndState(toDate, Date.now());
  if (!state) return null;

  const el = document.createElement('span');
  el.className = 'bs-sale-end' + (o.className ? ' ' + o.className : '');
  el.dataset.bsSaleTo = String(toDate);
  const variant = o.long ? 'long' : o.variant;
  if (variant && variant !== 'short' && VARIANTS.indexOf(variant) >= 0) el.dataset.bsSaleVariant = variant;
  el.appendChild(icon('clock', 'bs-sale-end__ic'));
  const text = document.createElement('span');
  text.className = 'bs-sale-end__text';
  el.appendChild(text);
  paint(el, state);

  LIVE.add(el);
  if (!timer) timer = setInterval(tickAll, TICK_MS);
  return el;
}

// ---------------------------------------------------------------------------
// Data: konec akce po kódech z `/specs` (sdílená cache specsCache.js — tentýž
// GET nese i parametry chipů a příznak Bestseller)
// ---------------------------------------------------------------------------

function toDateOf(item) {
  return item && item.action && typeof item.action.to === 'string' ? item.action.to : null;
}

/**
 * Konce akcí pro dávku kódů. Vrací `Map<code, toDate|null>` — `null` = produkt
 * konec nemá (nebo API mlčí; obojí znamená „nekreslit"). Nikdy nehází.
 */
export async function saleEndsFor(codes) {
  const out = new Map();
  if (!getRetailConfig()) return out;
  const items = await specsFor(codes);
  items.forEach(function (item, code) {
    out.set(code, toDateOf(item));
  });
  return out;
}

/** Konec akce jednoho kódu (`toDate` nebo `null`). Nikdy nehází. */
export async function saleEndFor(code) {
  if (!code) return null;
  const map = await saleEndsFor([code]);
  return map.get(code) || null;
}

/** Jen pro testy: podstrčit známý konec bez sítě. */
export function primeSaleEnd(code, toDate) {
  primeSpecs(code, { code: code, info: [], filter: [], action: { from: null, to: toDate } });
}
